/**
 * TripDashboard - Admin trips listing with scope tabs and table view.
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import styled from "styled-components";
import {
  Alert,
  Snackbar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Collapse,
} from "@mui/material";
import {
  Add as AddIcon,
  FilterList as FilterListIcon,
  Map as MapIcon,
  List as ListIcon,
  FileDownload as ExportIcon,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import CreateTripModal from "../components/CreateTripModal";
import TripsMapView from "../components/TripsMapView";
import TripsTable from "../components/TripsTable";
import { getTrips } from "../services/tripService";
import {
  ACCEPTING_LOAD_TRIP_STATUS,
  PLANNED_SCOPE_STATUSES,
  PLANNED_TRIP_STATUS,
  ARCHIVED_TRIP_STATUS,
  IN_PROGRESS_TRIP_STATUSES,
  TRIP_STATUS_LABELS,
} from "../constants/tripStatuses";
import { exportTripsToCsv } from "../utils/tripFormatters.js";
import { logError, logInfo } from "../../../shared/utils/logger.js";

const ACCENT = "#e3292f";
const PRIMARY = "#005249";

const Page = styled.div`
  padding: 24px 16px 40px;
  max-width: 1400px;
  margin: 0 auto;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const TitleBlock = styled.div``;

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: #111827;
`;

const Subtitle = styled.p`
  margin: 6px 0 0;
  color: #64748b;
  font-size: 14px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ToggleGroup = styled.div`
  display: inline-flex;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
`;

const ToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: ${({ $active }) => ($active ? ACCENT : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : "#374151")};

  &:hover {
    background: ${({ $active }) => ($active ? ACCENT : "#f8fafc")};
  }
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: ${ACCENT};
  color: #fff;

  &:hover {
    filter: brightness(0.95);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: #fff;
  color: #374151;

  &:hover {
    background: #f8fafc;
  }
`;

const ScopeTabs = styled.div`
  display: flex;
  gap: 24px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 20px;
`;

const ScopeTab = styled.button`
  border: none;
  background: none;
  padding: 0 0 12px;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  color: ${({ $active }) => ($active ? "#111827" : "#64748b")};
  cursor: pointer;
  border-bottom: 3px solid ${({ $active }) => ($active ? ACCENT : "transparent")};
  margin-bottom: -1px;

  &:hover {
    color: #111827;
  }
`;

const FiltersPanel = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 20px;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: flex-end;
`;

const ManageRoutesLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  margin-left: 8px;
  color: ${PRIMARY};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 320px;
`;

const SubScopeTabs = styled.div`
  display: flex;
  gap: 12px;
  margin: -8px 0 20px;
`;

const SubScopeTab = styled.button`
  border: 1px solid ${({ $active }) => ($active ? ACCENT : "#e5e7eb")};
  background: ${({ $active }) => ($active ? "#fff5f5" : "#fff")};
  color: ${({ $active }) => ($active ? ACCENT : "#64748b")};
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: ${ACCENT};
    color: ${ACCENT};
  }
`;

const DEFAULT_FILTERS = {
  status: "",
  routeCorridor: "",
  departureDate: "",
};

const buildApiFilters = (listScope, filters, plannedSubView = "upcoming") => {
  const apiFilters = {
    routeCorridor: filters.routeCorridor || undefined,
    departureDate: filters.departureDate || undefined,
  };

  if (listScope === "archived") {
    apiFilters.status = ARCHIVED_TRIP_STATUS;
    return apiFilters;
  }

  if (listScope === "planned") {
    if (plannedSubView === "past_due") {
      apiFilters.pastDue = true;
    } else {
      apiFilters.statusIn = PLANNED_SCOPE_STATUSES;
      apiFilters.pastDue = false;
    }
    return apiFilters;
  }

  if (filters.status) {
    apiFilters.status = filters.status;
  } else {
    apiFilters.statusIn = IN_PROGRESS_TRIP_STATUSES;
  }

  return apiFilters;
};

const TripDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const scopeFromUrl = searchParams.get("scope");
  const statusFromUrl = searchParams.get("status") || "";
  const plannedViewFromUrl = searchParams.get("plannedView");
  const initialPlannedSubView =
    plannedViewFromUrl === "past_due" ? "past_due" : "upcoming";
  const initialScope =
    scopeFromUrl === "archived" || statusFromUrl === ARCHIVED_TRIP_STATUS
      ? "archived"
      : scopeFromUrl === "planned" || statusFromUrl === PLANNED_TRIP_STATUS
        ? "planned"
        : "active";

  const [listScope, setListScope] = useState(initialScope);
  const [plannedSubView, setPlannedSubView] = useState(initialPlannedSubView);
  const [trips, setTrips] = useState([]);
  const [totalTrips, setTotalTrips] = useState(0);
  const [scopeCounts, setScopeCounts] = useState({
    active: 0,
    planned: 0,
    plannedPastDue: 0,
    archived: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState(() => ({
    status:
      initialScope === "active" &&
      statusFromUrl &&
      statusFromUrl !== ARCHIVED_TRIP_STATUS &&
      statusFromUrl !== PLANNED_TRIP_STATUS
        ? statusFromUrl
        : "",
    routeCorridor: searchParams.get("routeCorridor") || "",
    departureDate: searchParams.get("departureDate") || "",
  }));
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createPrefill, setCreatePrefill] = useState(null);
  const [viewMode, setViewMode] = useState("list");

  useEffect(() => {
    if (searchParams.get("create") !== "1") return;

    const assignOrderId = searchParams.get("assignOrderId");
    const assignRoute = searchParams.get("assignRoute");

    setCreatePrefill({
      orderIds: assignOrderId ? [assignOrderId] : [],
      routeCorridor: assignRoute || "",
    });
    setCreateModalOpen(true);

    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        params.delete("create");
        params.delete("assignOrderId");
        params.delete("assignRoute");
        return params;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        if (listScope === "archived") {
          params.set("scope", "archived");
          params.delete("status");
        } else if (listScope === "planned") {
          params.set("scope", "planned");
          if (plannedSubView === "past_due") {
            params.set("plannedView", "past_due");
          } else {
            params.delete("plannedView");
          }
          params.delete("status");
        } else {
          params.delete("scope");
          params.delete("plannedView");
          if (filters.status) {
            params.set("status", filters.status);
          } else {
            params.delete("status");
          }
        }
        if (filters.routeCorridor) {
          params.set("routeCorridor", filters.routeCorridor);
        } else {
          params.delete("routeCorridor");
        }
        if (filters.departureDate) {
          params.set("departureDate", filters.departureDate);
        } else {
          params.delete("departureDate");
        }
        return params;
      },
      { replace: true }
    );
  }, [filters, listScope, plannedSubView, setSearchParams]);

  const apiFilters = useMemo(
    () => buildApiFilters(listScope, filters, plannedSubView),
    [listScope, filters, plannedSubView]
  );

  const sharedCountFilters = useMemo(
    () => ({
      routeCorridor: filters.routeCorridor || undefined,
      departureDate: filters.departureDate || undefined,
    }),
    [filters.routeCorridor, filters.departureDate]
  );

  const fetchScopeCounts = useCallback(async () => {
    try {
      const [activeRes, plannedRes, plannedPastDueRes, archivedRes] = await Promise.all([
        getTrips(
          { ...sharedCountFilters, statusIn: IN_PROGRESS_TRIP_STATUSES },
          1,
          1
        ),
        getTrips(
          {
            ...sharedCountFilters,
            statusIn: PLANNED_SCOPE_STATUSES,
            pastDue: false,
          },
          1,
          1
        ),
        getTrips({ ...sharedCountFilters, pastDue: true }, 1, 1),
        getTrips(
          { ...sharedCountFilters, status: ARCHIVED_TRIP_STATUS },
          1,
          1
        ),
      ]);
      setScopeCounts({
        active: activeRes.data?.total ?? 0,
        planned: plannedRes.data?.total ?? 0,
        plannedPastDue: plannedPastDueRes.data?.total ?? 0,
        archived: archivedRes.data?.total ?? 0,
      });
    } catch (err) {
      logError("Failed to fetch trip scope counts", { error: err.message });
    }
  }, [sharedCountFilters]);

  const fetchTrips = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getTrips(apiFilters, page, limit);
      if (response.success && response.data) {
        setTrips(response.data.trips || []);
        setTotalTrips(response.data.total ?? response.data.trips?.length ?? 0);
        logInfo("Trips fetched successfully", {
          count: response.data.trips?.length || 0,
          scope: listScope,
        });
      } else {
        throw new Error(response.error || "Failed to fetch trips");
      }
    } catch (err) {
      logError("Failed to fetch trips", {
        apiFilters,
        scope: listScope,
        error: err.message,
      });
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiFilters, listScope, page, limit]);

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  useEffect(() => {
    fetchScopeCounts();
  }, [fetchScopeCounts]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleScopeChange = (nextScope) => {
    if (nextScope === listScope) return;
    setListScope(nextScope);
    if (nextScope !== "planned") {
      setPlannedSubView("upcoming");
    }
    setFilters((prev) => ({ ...prev, status: "" }));
    setPage(1);
  };

  const handlePlannedSubViewChange = (nextView) => {
    if (nextView === plannedSubView) return;
    setPlannedSubView(nextView);
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({ ...DEFAULT_FILTERS });
    setPage(1);
  };

  const hasSecondaryFilters = Boolean(
    filters.routeCorridor ||
      filters.departureDate ||
      (listScope === "active" && filters.status)
  );

  const handleTripSelect = (trip) => {
    const tripId = trip?.id || trip?._id;
    if (!tripId) {
      setNotification({
        open: true,
        message: "Trip ID not found",
        severity: "error",
      });
      return;
    }
    navigate(`/trips/${tripId}`);
  };

  const handleExport = () => {
    if (!trips.length) {
      setNotification({
        open: true,
        message: "No trips to export",
        severity: "warning",
      });
      return;
    }
    exportTripsToCsv(trips);
  };

  if (loading && trips.length === 0) {
    return (
      <Page>
        <LoadingWrap>
          <CircularProgress />
        </LoadingWrap>
      </Page>
    );
  }

  return (
    <Page>
      <HeaderRow>
        <TitleBlock>
          <Title>Trips</Title>
          <Subtitle>
            Manage and monitor all trips across the network.
            <ManageRoutesLink
              type="button"
              onClick={() => navigate("/trips/route-corridors")}
            >
              Manage routes
            </ManageRoutesLink>
          </Subtitle>
        </TitleBlock>

        <Actions>
          <ToggleGroup>
            <ToggleButton
              type="button"
              $active={viewMode === "list"}
              onClick={() => setViewMode("list")}
            >
              <ListIcon fontSize="small" /> List
            </ToggleButton>
            <ToggleButton
              type="button"
              $active={viewMode === "map"}
              onClick={() => setViewMode("map")}
            >
              <MapIcon fontSize="small" /> Map
            </ToggleButton>
          </ToggleGroup>

          <PrimaryButton type="button" onClick={() => setCreateModalOpen(true)}>
            <AddIcon fontSize="small" /> New Trip
          </PrimaryButton>

          <SecondaryButton
            type="button"
            onClick={() => setFiltersOpen((open) => !open)}
          >
            <FilterListIcon fontSize="small" />
            Filters
            {hasSecondaryFilters ? ` (${[
              listScope === "active" && filters.status,
              filters.routeCorridor,
              filters.departureDate,
            ].filter(Boolean).length})` : ""}
          </SecondaryButton>

          <SecondaryButton type="button" onClick={handleExport}>
            <ExportIcon fontSize="small" /> Export
          </SecondaryButton>
        </Actions>
      </HeaderRow>

      <ScopeTabs>
        <ScopeTab
          type="button"
          $active={listScope === "active"}
          onClick={() => handleScopeChange("active")}
        >
          Active Trips ({scopeCounts.active})
        </ScopeTab>
        <ScopeTab
          type="button"
          $active={listScope === "planned"}
          onClick={() => handleScopeChange("planned")}
        >
          Planned ({scopeCounts.planned})
        </ScopeTab>
        <ScopeTab
          type="button"
          $active={listScope === "archived"}
          onClick={() => handleScopeChange("archived")}
        >
          Archived ({scopeCounts.archived})
        </ScopeTab>
      </ScopeTabs>

      {listScope === "planned" ? (
        <SubScopeTabs>
          <SubScopeTab
            type="button"
            $active={plannedSubView === "upcoming"}
            onClick={() => handlePlannedSubViewChange("upcoming")}
          >
            Upcoming ({scopeCounts.planned})
          </SubScopeTab>
          <SubScopeTab
            type="button"
            $active={plannedSubView === "past_due"}
            onClick={() => handlePlannedSubViewChange("past_due")}
          >
            Past due ({scopeCounts.plannedPastDue})
          </SubScopeTab>
        </SubScopeTabs>
      ) : null}

      {listScope === "archived" ? (
        <Subtitle style={{ marginTop: -8, marginBottom: 20 }}>
          Shows trips ops marked completed — never auto-archived by calendar.
        </Subtitle>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Collapse in={filtersOpen || hasSecondaryFilters}>
        <FiltersPanel>
          <FiltersRow>
            {listScope === "active" ? (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  <MenuItem value="">All In Progress</MenuItem>
                  {IN_PROGRESS_TRIP_STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      {TRIP_STATUS_LABELS[status]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Route</InputLabel>
              <Select
                value={filters.routeCorridor}
                label="Route"
                onChange={(e) =>
                  handleFilterChange("routeCorridor", e.target.value)
                }
              >
                <MenuItem value="">All Routes</MenuItem>
                <MenuItem value="WC-EC">WC-EC</MenuItem>
                <MenuItem value="WC-EC-KZN">WC-EC-KZN</MenuItem>
                <MenuItem value="WC-GP-KZN">WC-GP-KZN</MenuItem>
                <MenuItem value="GP-KZN">GP-KZN</MenuItem>
                <MenuItem value="GP-WC">GP-WC</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="date"
              label="Departure Date"
              value={filters.departureDate}
              onChange={(e) =>
                handleFilterChange("departureDate", e.target.value)
              }
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180 }}
            />
            {hasSecondaryFilters ? (
              <SecondaryButton type="button" onClick={handleClearFilters}>
                Clear filters
              </SecondaryButton>
            ) : null}
          </FiltersRow>

          {hasSecondaryFilters ? (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
              {listScope === "active" && filters.status ? (
                <Chip
                  label={`Status: ${TRIP_STATUS_LABELS[filters.status] || filters.status}`}
                  size="small"
                  onDelete={() => handleFilterChange("status", "")}
                />
              ) : null}
              {filters.routeCorridor ? (
                <Chip
                  label={`Route: ${filters.routeCorridor}`}
                  size="small"
                  onDelete={() => handleFilterChange("routeCorridor", "")}
                />
              ) : null}
              {filters.departureDate ? (
                <Chip
                  label={`Date: ${new Date(filters.departureDate).toLocaleDateString()}`}
                  size="small"
                  onDelete={() => handleFilterChange("departureDate", "")}
                />
              ) : null}
            </div>
          ) : null}
        </FiltersPanel>
      </Collapse>

      {viewMode === "map" ? (
        <TripsMapView
          trips={trips}
          filters={filters}
          onTripClick={handleTripSelect}
          onDropClick={(drop, trip) => handleTripSelect(trip)}
        />
      ) : (
        <>
          <TripsTable
            trips={trips}
            onView={handleTripSelect}
            onManageDrops={(trip) => {
              const tripId = trip?.id || trip?._id;
              if (tripId) navigate(`/trips/${tripId}/drops`);
            }}
          />
          {totalTrips > trips.length ? (
            <Subtitle style={{ marginTop: 12 }}>
              Showing {trips.length} of {totalTrips} trips
            </Subtitle>
          ) : null}
        </>
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <CreateTripModal
        open={createModalOpen}
        prefill={createPrefill}
        onSuccess={(trip) => {
          const prefillOrderIds = createPrefill?.orderIds || [];
          setCreateModalOpen(false);
          setCreatePrefill(null);
          setNotification({
            open: true,
            message: "Trip created successfully",
            severity: "success",
          });
          fetchTrips();
          fetchScopeCounts();
          if (window.opener && !window.opener.closed && trip?.id) {
            window.opener.postMessage(
              {
                type: "trip-created",
                tripId: trip.id,
                orderIds: trip.orders || prefillOrderIds,
              },
              window.location.origin
            );
          }
          if (trip?.id && !window.opener) {
            navigate(`/trips/${trip.id}`);
          }
        }}
        onCancel={() => {
          setCreateModalOpen(false);
          setCreatePrefill(null);
        }}
      />
    </Page>
  );
};

export default TripDashboard;
